import React from 'react'
import { FavoriteItem } from '@plm-core/favorites'
import {
  PersonGroupIcon,
  ShipBoxIcon,
  WorkspaceIcon,
  TemplateIcon,
  JoinRightIcon,
  SwatchesIcon,
  MerchHouseholdPaperIcon,
} from '@design-systems/react-icons'
import _ from 'lodash'
import { envConfigs } from 'apiConfig'
import { FavoriteEntityConfig } from '@plm-core/favorites'
import getAttachmentUrl from '../utils/getAttachmentUrl'

const getImageUrl = ({ attachmentId }: { attachmentId: string }) => {
  return getAttachmentUrl({ attachmentId, size: 68 })
}

const GROUPED_MATERIAL_TYPES = [
  'colors',
  'colors_archroma',
  'colors_pantone',
  'colors_coloro',
  'artworks',
  'fabric',
  'fabric_spec',
  'digital_fabric',
  'target_colors',
  'reference_colors',
  'target_colors_archroma',
  'reference_colors_archroma',
  'target_colors_pantone',
  'reference_colors_pantone',
  'target_colors_coloro',
  'reference_colors_coloro',
  'trim',
  'wash',
  'wood',
  'paper based substrate',
  'material',
]

const materialConfig = {
  favoriteEntity: 'material',
  groupedEntities: [...GROUPED_MATERIAL_TYPES],
  customQueryVariables: { types: GROUPED_MATERIAL_TYPES },
}

export const defaultEntityConfig = {
  product: {
    favoriteEntity: 'product',
    customQueryVariables: { types: ['product'] },
  },
  product_template: {
    favoriteEntity: 'product_template',
    customQueryVariables: { types: ['product_template'] },
  },
  workspace: {
    favoriteEntity: 'workspace',
    customQueryVariables: { types: ['workspace'] },
  },
  team: {
    favoriteEntity: 'team',
    customQueryVariables: { types: ['team'] },
  },
  combination: {
    favoriteEntity: 'combination',
    customQueryVariables: { types: ['combination'] },
  },
  palette: {
    favoriteEntity: 'palette',
    customQueryVariables: { types: ['palette'] },
  },
  material: {
    ...materialConfig,
  },
}

export const materialEntityConfig = _.reduce(
  GROUPED_MATERIAL_TYPES,
  (acc, config) => {
    acc[config] = materialConfig
    return acc
  },
  {} as any,
)

export const allPlmFavoriteEntityConfig = {
  ...defaultEntityConfig,
  ...materialEntityConfig,
}

export const plmFavoriteMenuEntityConfig = (
  environment: keyof typeof envConfigs,
): FavoriteEntityConfig[] => [
  {
    tabTitle: 'Products',
    ...allPlmFavoriteEntityConfig.product,
    getFavoriteUrl: (contentItem: any) => `/products/${contentItem.resourceId}`,
    getRowItemData: (contentItem: any) => (
      <FavoriteItem
        id={contentItem?.resourceId}
        bodyText={`${contentItem?.resourceId}${contentItem?.resource?.description ? ` - ${contentItem?.resource?.description}` : ''}`}
        imageUrl={
          contentItem?.resource?.primaryAttachmentResourceId
            ? getImageUrl({
                attachmentId:
                  contentItem?.resource?.primaryAttachmentResourceId,
              })
            : undefined
        }
        iconColor="var(--color-data-negative-base)"
        canvasSvgIcon={ShipBoxIcon}
      />
    ),
  },
  {
    tabTitle: 'Workspaces',
    ...allPlmFavoriteEntityConfig.workspace,
    getFavoriteUrl: (contentItem: any) =>
      `/workspaces/${contentItem.resourceId}/products`,
    getRowItemData: (contentItem: any) => (
      <FavoriteItem
        variant="dense"
        id={contentItem?.resourceId}
        bodyText={contentItem?.resource?.description}
        iconColor="var(--color-data-categorical-8-base)"
        canvasSvgIcon={WorkspaceIcon}
      />
    ),
  },
  {
    tabTitle: 'Templates',
    ...allPlmFavoriteEntityConfig.product_template,
    getFavoriteUrl: (contentItem: any) =>
      `/templateLibrary/productTemplates/${contentItem.resourceId}`,
    getRowItemData: (contentItem: any) => (
      <FavoriteItem
        id={contentItem?.resourceId}
        bodyText={`${contentItem?.resourceId}${contentItem?.resource?.description ? ` - ${contentItem?.resource?.description}` : ''}`}
        imageUrl={
          contentItem?.resource?.primaryAttachmentResourceId
            ? getImageUrl({
                attachmentId:
                  contentItem?.resource?.primaryAttachmentResourceId,
              })
            : undefined
        }
        iconColor="var(--color-background-warning-base)"
        canvasSvgIcon={TemplateIcon}
      />
    ),
  },
  {
    tabTitle: 'Materials',
    ...allPlmFavoriteEntityConfig.material,
    getFavoriteUrl: (contentItem: any) =>
      `/resourceRedirect/${contentItem?.resourceId}`,
    getRowItemData: (contentItem: any) => (
      <FavoriteItem
        id={contentItem?.resourceId}
        bodyText={`${contentItem?.resource?.description || contentItem?.resource?.name || ''}`}
        imageUrl={
          contentItem?.resource?.primaryAttachment?.id
            ? getImageUrl({
                attachmentId: contentItem?.resource?.primaryAttachment?.id,
              })
            : undefined
        }
        iconColor="var(--color-background-success-base)"
        canvasSvgIcon={MerchHouseholdPaperIcon}
      />
    ),
  },
  {
    tabTitle: 'Combos',
    ...allPlmFavoriteEntityConfig.combination,
    getFavoriteUrl: (contentItem: any) =>
      `/combinations/${contentItem.resourceId}`,
    getRowItemData: (contentItem: any) => (
      <FavoriteItem
        variant="dense"
        id={contentItem?.resourceId}
        bodyText={`${contentItem?.resourceId}${contentItem?.resource?.description ? ` - ${contentItem?.resource?.description}` : ''}`}
        iconColor="var(--color-data-secondary-base)"
        canvasSvgIcon={JoinRightIcon}
      />
    ),
  },
  {
    tabTitle: 'Palettes',
    ...allPlmFavoriteEntityConfig.palette,
    getFavoriteUrl: (contentItem: any) =>
      `/palettes/${contentItem?.resourceId}`,
    getRowItemData: (contentItem: any) => (
      <FavoriteItem
        variant="dense"
        id={contentItem?.resourceId}
        bodyText={`${contentItem?.resource?.description || ''}`}
        imageUrl={
          contentItem?.resource?.primaryAttachment?.id
            ? getImageUrl({
                attachmentId: contentItem?.resource?.primaryAttachment?.id,
              })
            : undefined
        }
        iconColor="var(--color-data-categorical-4-base)"
        canvasSvgIcon={SwatchesIcon}
      />
    ),
  },
  {
    tabTitle: 'Teams',
    ...allPlmFavoriteEntityConfig.team,
    getFavoriteUrl: (contentItem: any) => `/teams/${contentItem?.resourceId}`,
    getRowItemData: (contentItem: any) => (
      <FavoriteItem
        variant="dense"
        id={contentItem?.resourceId}
        bodyText={contentItem?.team?.name}
        iconColor="var(--color-background-brand-base-hover)"
        canvasSvgIcon={PersonGroupIcon}
      />
    ),
  },
]
